import { Helmet } from 'react-helmet-async'
import { seoConfig, ogImages } from '../config/seo'

/**
 * Centralised SEO component for consistent meta tags across all pages
 *
 * @param {Object} props
 * @param {string} props.title - Page title (falls back to seoConfig.defaultTitle)
 * @param {string} props.description - Meta description (falls back to seoConfig.defaultDescription)
 * @param {string} props.canonical - Canonical URL path (e.g., '/blog/my-post')
 * @param {string} props.type - OG type: 'website', 'article', or 'profile' (default: 'website')
 * @param {string} props.image - OG image URL (falls back to seoConfig.ogImage)
 * @param {boolean} props.noindex - If true, adds noindex,nofollow robots meta
 * @param {Object|Object[]} props.structuredData - JSON-LD schema object(s) to inject
 * @param {Object} props.article - Article-specific meta: { publishedTime, modifiedTime, section, tags }
 * @param {React.ReactNode} props.children - Additional Helmet children for custom tags
 */
const SEO = ({
  title,
  description,
  canonical,
  type = 'website',
  image,
  noindex = false,
  structuredData,
  article,
  children,
}) => {
  const siteUrl = seoConfig.siteUrl
  const fullTitle = title || seoConfig.defaultTitle
  const fullDescription = description || seoConfig.defaultDescription

  // Build canonical URL, auto-generating from current path if not provided
  // Always strips trailing slashes for consistency
  const getCanonicalUrl = () => {
    if (canonical) {
      // Strip trailing slash and ensure proper format
      const cleanPath = canonical.replace(/\/$/, '')
      if (cleanPath.startsWith('http')) {
        return cleanPath
      }
      return `${siteUrl}${cleanPath.startsWith('/') ? cleanPath : `/${cleanPath}`}`
    }
    // Auto-generate from current pathname (strip trailing slash)
    if (typeof window !== 'undefined') {
      const path = window.location.pathname.replace(/\/$/, '')
      return `${siteUrl}${path}`
    }
    return null
  }

  const canonicalUrl = getCanonicalUrl()

  // Use provided image or fall back to default
  const ogImage = image || ogImages.default

  return (
    <Helmet>
      {/* Basic Meta */}
      <title>{fullTitle}</title>
      <meta name="description" content={fullDescription} />

      {/* Canonical URL */}
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}

      {/* Robots */}
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph */}
      <meta property="og:site_name" content={seoConfig.siteName} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={fullDescription} />
      <meta property="og:type" content={type} />
      {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:locale" content={seoConfig.locale} />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={fullDescription} />
      <meta name="twitter:image" content={ogImage} />

      {/* Article-specific Open Graph */}
      {article && article.publishedTime && (
        <meta property="article:published_time" content={article.publishedTime} />
      )}
      {article && article.modifiedTime && (
        <meta property="article:modified_time" content={article.modifiedTime} />
      )}
      {article && <meta property="article:author" content="TeeSync" />}
      {article && article.section && (
        <meta property="article:section" content={article.section} />
      )}
      {article && article.tags && article.tags.map((tag, index) => (
        <meta key={`article-tag-${index}`} property="article:tag" content={tag} />
      ))}

      {/* Structured Data (JSON-LD) */}
      {structuredData && (
        Array.isArray(structuredData) ? (
          structuredData.map((data, index) => (
            <script key={`ld-${index}`} type="application/ld+json">
              {JSON.stringify(data)}
            </script>
          ))
        ) : (
          <script type="application/ld+json">
            {JSON.stringify(structuredData)}
          </script>
        )
      )}

      {/* Additional custom tags */}
      {children}
    </Helmet>
  )
}

export default SEO
